import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, NavLink } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ChatProvider } from '@/context/ChatContext';
import { ProjectProvider } from '@/context/ProjectContext';
import { RecipeProvider } from '@/context/RecipeContext';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ChatPage } from '@/pages/ChatPage';
import { LoginPage } from '@/pages/LoginPage';
import { ProjectsPage } from '@/pages/ProjectsPage';
import { ProjectDetailPage } from '@/pages/ProjectDetailPage';
import { RecipesPage } from '@/pages/RecipesPage';
import { InterviewWizard } from '@/pages/InterviewWizard';
import { cn } from '@/lib/utils';

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-brand border-t-transparent mx-auto mb-4" />
          <p className="text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function NavTabs() {
  const tabs = [
    { to: '/', label: 'Chat', icon: 'chat' },
    { to: '/projects', label: 'Projects', icon: 'folder' },
    { to: '/recipes', label: 'Recipes', icon: 'auto_awesome' },
  ];

  return (
    <nav className="flex items-center gap-1 px-4 py-2 bg-elevated border-b border-zinc-800">
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              isActive
                ? 'bg-brand/20 text-brand'
                : 'text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800'
            )
          }
        >
          <span className="material-icons text-lg">{tab.icon}</span>
          {tab.label}
        </NavLink>
      ))}
    </nav>
  );
}

function MainLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-base flex flex-col">
      <NavTabs />
      <main className="flex-1 overflow-hidden">{children}</main>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ChatProvider>
                <ChatPage />
              </ChatProvider>
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectsPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/projects/:id"
        element={
          <ProtectedRoute>
            <MainLayout>
              <ProjectDetailPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes"
        element={
          <ProtectedRoute>
            <MainLayout>
              <RecipesPage />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/recipes/:slug/interview"
        element={
          <ProtectedRoute>
            <MainLayout>
              <InterviewWizard />
            </MainLayout>
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <ProjectProvider>
            <RecipeProvider>
              <AppRoutes />
            </RecipeProvider>
          </ProjectProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
